import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import {
  type OpenNewIssueFromPreviewMutation,
  type OpenNewIssueFromPreviewMutationVariables,
  PreviewMessageType,
} from "@medbillai/graphql-types";

import type { CaseType } from "@medbillai/schemas";

import { useAlerts } from "../provider/alerts";
import {
  getIssueQuery,
  getIssueQueryVariables,
} from "../screens/case/utils/queries";
import {
  getIssuesQueryV2,
  openNewIssueFromPreviewMutation,
} from "../screens/cases/utils/queries";
import { showWelcomeScreenQuery } from "../screens/home/utils/queries";
import { analyticsService } from "./analytics";
import type { EventTypes } from "./analytics/types";
import { errorHandling } from "./apollo/utils";
import { useAuth } from "./auth";
import { SingularEvents, singularService } from "./singular";

type CreateIssueAnalyticsPayload = EventTypes["CreateIssue"];

export type UseCreateIssueParams = {
  caseType: CaseType;
  title: string;
  existingMessages: OpenNewIssueFromPreviewMutationVariables["input"]["messages"];
  analyticsProperties: CreateIssueAnalyticsPayload;
};

export type OpenIssueInput = { userMessage: string } | { documentId: string };

export function useCreateIssue({
  caseType,
  title,
  existingMessages,
  analyticsProperties,
}: UseCreateIssueParams) {
  const { user } = useAuth();
  const { showAlert } = useAlerts();

  const [openIssueFromPreview, { loading }] = useMutation(
    openNewIssueFromPreviewMutation,
    {
      context: errorHandling("caller"),
      update: (cache, { data: mutData }) => {
        const openNewIssue = mutData?.openNewIssueFromPreview;
        if (!openNewIssue) {
          return;
        }
        // Write to cache with the real ID
        cache.writeQuery({
          query: getIssueQuery,
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          variables: getIssueQueryVariables(openNewIssue.id, user?.id!),
          data: {
            __typename: "Query",
            issue__Issue: [openNewIssue],
            me: {
              __typename: "User_Type",
              id: user!.id,
              webAgentLinks: [],
            },
          },
        });
      },
      refetchQueries: [
        {
          query: showWelcomeScreenQuery,
        },
        {
          query: getIssuesQueryV2,
        },
      ],
    },
  );

  const newIssue = useCallback(
    async (input: OpenIssueInput): Promise<OpenNewIssueFromPreviewMutation> => {
      analyticsService.trackEvent("CreateIssue", analyticsProperties);
      singularService.event(SingularEvents.createCase);

      const newMessages: OpenNewIssueFromPreviewMutationVariables["input"]["messages"] =
        [];
      if ("userMessage" in input) {
        newMessages.push({
          type: PreviewMessageType.USER,
          userMessageInfo: {
            message: input.userMessage,
          },
        });
      } else {
        newMessages.push({
          type: PreviewMessageType.USER,
          userMessageInfo: {
            documentId: input.documentId,
          },
        });
      }

      const { data: newData } = await openIssueFromPreview({
        variables: {
          input: {
            title,
            caseType,
            messages: [...existingMessages, ...newMessages],
            isFromUpload: false,
          },
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          userId: user?.id!,
        },
      });
      if (!newData) {
        throw new Error("Failed to open new issue");
      }

      return newData;
    },
    [
      title,
      openIssueFromPreview,
      caseType,
      user?.id,
      existingMessages,
      analyticsProperties,
    ],
  );

  const openIssue = useCallback(
    async (input: OpenIssueInput) => {
      try {
        const newData = await newIssue(input);
        const newId = newData?.openNewIssueFromPreview?.id;
        return newId;
      } catch (err) {
        console.error("Error creating issue", err);
        showAlert({
          type: "error",
          message: "Something went wrong. Please try again later.",
        });
      }
    },
    [newIssue, showAlert],
  );

  return {
    openIssue,
    loading,
  };
}
